import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Calendar, Clock, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import { getBlogBySlug } from '../../data/blogs';

const Blog26032026 = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1100);
  const blog = getBlogBySlug('can-decentralized-hiring-through-global-coding');

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = document.querySelectorAll('h2[id]');
      let current = '';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute('id') || '';
        }
      });

      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1100);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  const tableOfContents = [
    {
        "id": "context",
        "title": "1. Context",
        "originalTitle": "Context"
    },
    {
        "id": "problem-statement",
        "title": "2. Problem Statement",
        "originalTitle": "Problem Statement"
    },
    {
        "id": "core-framework-skill-based-decentralized-talent-sourcing",
        "title": "3. Core Framework: Skill-Based Decentralized Talent Sourcing",
        "originalTitle": "Core Framework: Skill-Based Decentralized Talent Sourcing"
    },
    {
        "id": "comparative-analysis",
        "title": "4. Comparative Analysis",
        "originalTitle": "Comparative Analysis"
    },
    {
        "id": "business-use-cases",
        "title": "5. Business Use Cases",
        "originalTitle": "Business Use Cases"
    },
    {
        "id": "benefits-outcomes",
        "title": "6. Benefits & Outcomes",
        "originalTitle": "Benefits & Outcomes"
    },
    {
        "id": "challenges-realities",
        "title": "7. Challenges & Realities",
        "originalTitle": "Challenges & Realities"
    },
    {
        "id": "future-outlook",
        "title": "8. Future Outlook",
        "originalTitle": "Future Outlook"
    },
    {
        "id": "conclusion",
        "title": "9. Conclusion",
        "originalTitle": "Conclusion"
    },
    {
        "id": "call-to-action",
        "title": "10. Call to Action",
        "originalTitle": "Call to Action"
    }
];

  const faqData = [
    {
        "question": "What is this technology and how does it work?",
        "answer": "This technology represents a significant advancement in the field, offering innovative solutions to common challenges through modern approaches and proven methodologies."
    },
    {
        "question": "Who can benefit from implementing this solution?",
        "answer": "Organizations of all sizes can benefit, particularly those looking to improve efficiency, reduce costs, and enhance their competitive advantage through technological innovation."
    },
    {
        "question": "What are the main challenges in implementation?",
        "answer": "Key challenges include initial setup complexity, integration with existing systems, and ensuring proper training. However, with proper planning and support, these can be effectively managed."
    },
    {
        "question": "What ROI can be expected?",
        "answer": "While results vary by organization, typical implementations show significant improvements in operational efficiency, cost reduction, and enhanced capabilities within the first year."
    }
];

  const tldrPoints = [
    "In an increasingly competitive global talent landscape, traditional recruitment methods are often struggling to keep pace with the demand for highly s...",
    "Traditional hiring processes are frequently plagued by operational inefficiencies, high costs, and subjective biases. Companies often face lengthy tim...",
    "Skill-Based Decentralized Talent Sourcing is an innovative HR strategy that leverages global, meritocratic platforms such as coding contests or specia..."
];

  const keyTakeaways = [
    "Fast implementation strategies deliver measurable ROI within weeks, not months",
    "Agile methodologies reduce time-to-production by 60-80% compared to traditional approaches",
    "Cloud-native architecture enables rapid scaling without infrastructure bottlenecks",
    "Automated workflows eliminate manual bottlenecks and accelerate delivery timelines",
    "Real-time analytics provide immediate insights for faster decision-making"
];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog?.title,
        "description": blog?.excerpt,
        "image": blog?.featuredImage,
        "datePublished": blog?.publishDate,
        "dateModified": blog?.publishDate,
        "author": {
          "@type": "Organization",
          "name": "Agility"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        },
        "keywords": blog?.seoKeywords.join(', '),
        "articleBody": "Can Decentralized Hiring Through Global Coding Contests Revolutionize Your Talent Acquisition Strategy?",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.agilitytech.ai/insights/${blog?.slug}`
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": [".tldr-section", ".key-takeaways-section"]
        }
      },
      {
        "@type": "HowTo",
        "name": blog?.title,
        "description": blog?.excerpt,
        "step": keyTakeaways.map((takeaway, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": `Step ${index + 1}`,
          "text": takeaway
        }))
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  if (!blog) return null;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <SEOHead
        title={`${blog?.title} | Agility Blog`}
        description={blog?.excerpt}
        keywords={blog?.seoKeywords.join(', ')}
        url={`https://www.agilitytech.ai/insights/${blog?.slug}`}
        image={blog?.featuredImage}
        type="article"
        article={true}
        author={blog?.author}
        publishedTime={new Date(blog?.publishDate || '').toISOString()}
        modifiedTime={new Date(blog?.publishDate || '').toISOString()}
        structuredData={structuredData}
      />

      {/* Blog Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to bottom, #0B0B0B, #050505)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="mb-6">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(20, 184, 166, 0.15)',
                color: '#14b8a6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: '1px solid rgba(20, 184, 166, 0.3)'
              }}>
                {blog.category}
              </span>
            </div>

            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#9ca3af', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span style={{ fontSize: '0.875rem' }}>{new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span style={{ fontSize: '0.875rem' }}>{blog.publishDate}</span>
              </div>
            </div>

            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: '#d1d5db',
              lineHeight: '1.75',
              marginBottom: '2rem'
            }}>
              {blog.excerpt}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {blog.tags.map((tag, index) => (
                <span key={index} style={{
                  padding: '0.25rem 0.75rem',
                  background: '#121212',
                  color: '#14b8a6',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #262626'
                }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section style={{ background: '#050505', paddingBottom: '5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', display: 'flex', minHeight: '100vh', gap: '3rem', flexDirection: isDesktop ? 'row' : 'column' }}>
          {/* Sidebar TOC - Desktop */}
          {isDesktop && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
              width: '260px',
              flexShrink: 0,
              zIndex: 999
            }}>
              <h3 style={{
                color: '#ffffff',
                fontWeight: '600',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Table of Contents</h3>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      transition: 'all 0.2s',
                      borderLeft: activeSection === item.id ? '2px solid #14b8a6' : '2px solid transparent',
                      background: activeSection === item.id ? 'rgba(20,184,166,0.15)' : 'transparent',
                      color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                      cursor: 'pointer',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#d1d5db';
                        e.currentTarget.style.background = '#121212';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#9ca3af';
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Mobile TOC */}
          {!isDesktop && (
            <div style={{
              width: '100%',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: '#121212',
                borderRadius: '12px',
                border: '1px solid #262626',
                padding: '1rem'
              }}>
                <button
                  onClick={() => setTocOpen(!tocOpen)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <span>Table of Contents</span>
                  <ChevronDown style={{
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }} />
                </button>
                {tocOpen && (
                  <nav style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          background: 'none',
                          border: 'none',
                          color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                )}
              </div>
            </div>
          )}

          {/* Article Content */}
          <div style={{ flex: 1 }}>
            <article style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>

              {/* TL;DR Section - AEO Optimized */}
              <div className="tldr-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>TL;DR</span>
                  Quick Summary
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {tldrPoints.map((point, idx) => (
                    <li key={idx} style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.25rem',
                        lineHeight: '1'
                      }}>•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              
              <h2 id="context" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Context
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                In an increasingly competitive global talent landscape, traditional recruitment methods are often struggling to keep pace with the demand for highly skilled engineers and specialists. The emergence of unconventional, merit-based approaches, exemplified by industry leaders like Pavel Durov, is challenging the status quo and redefining what’s possible in team building and talent acquisition. This shift towards decentralized hiring and skill-based selection is gaining traction as companies seek more efficient, objective, and innovative techniques to identify and secure top-tier talent, moving beyond the limitations of resumes and standard interviews.
              </p>

              <h2 id="problem-statement" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Problem Statement
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Traditional hiring processes are frequently plagued by operational inefficiencies, high costs, and subjective biases. Companies often face lengthy time-to-hire cycles, significant expenditure on recruitment agencies and HR department overheads, and the risk of mis-hires dueating to an over-reliance on credentials rather than demonstrable skills. The average cost-per-hire for a skilled engineer can easily exceed $15,000, with processes often stretching beyond 60 days. This not only impacts project timelines but also hinders innovation and company culture, leading to suboptimal team structures and increased talent development burdens.
              </p>

              <h2 id="core-framework-skill-based-decentralized-talent-sourcing" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Core Framework: Skill-Based Decentralized Talent Sourcing
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Skill-Based Decentralized Talent Sourcing is an innovative HR strategy that leverages global, meritocratic platforms such as coding contests or specialized challenges to identify, evaluate, and recruit top-tier talent. It bypasses conventional recruitment channels, focusing purely on demonstrated problem-solving abilities and technical prowess rather than resumes, academic backgrounds, or traditional interviews. This technique aims to build small, highly efficient teams, much like a "Navy SEAL unit," as described by Pavel Durov for his core engineering team at Telegram.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The process typically begins with the host company defining specific, complex technical challenges or problems relevant to their operational needs. These challenges are then launched on global competition platforms (e.g., Contest.com, as used by Durov). Engineers from around the world participate, submitting solutions that are rigorously evaluated based on criteria such as efficiency, correctness, innovation, and scalability. The top performers, identified solely by their objective results, are then invited to join the organization, often directly into critical engineering roles. This method allows for a "zero HR department" model, streamlining recruitment and significantly reducing associated costs.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                While highly effective for specific roles, particularly in software engineering and data science, this framework may have limitations for positions requiring extensive interpersonal skills, client management, or leadership capabilities that are harder to assess in a purely competitive technical environment. It also requires a robust platform and clear, challenging problems to effectively differentiate candidates. Furthermore, the limited direct interaction with candidates before selection might pose integration challenges into existing company culture for some.
              </p>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Core Framework: Skill-Based Decentralized Talent Sourcing
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Core Framework: Skill-Based Decentralized Talent Sourcing"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of core framework: skill-based decentralized talent sourcing concepts and implementation strategies.
                </p>
              </div>

              <h2 id="comparative-analysis" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Comparative Analysis
              </h2>

              <div style={{
                overflowX: 'auto',
                margin: '2rem 0',
                border: '1px solid #262626',
                borderRadius: '8px'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'clamp(0.875rem, 1.8vw, 1rem)',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  <thead>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Feature
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Traditional Hiring Process
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Skill-Based Decentralized Sourcing (e.g., Durov's Model)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Recruitment Cost
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        High (HR salaries, agency fees, advertising)
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Low (Platform fees, challenge creation)
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Time-to-Hire
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Long (Screening, multiple interviews, background checks)
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Short (Rapid identification of top performers)
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Talent Pool
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Limited by geographic location, network, job boards
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Global, meritocratic, highly diverse
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Selection Basis
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Resumes, interviews, experience, credentials
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Purely demonstrable skills, problem-solving ability, objective performance
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Bias Potential
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        High (Unconscious bias in screening, interviews)
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Low (Objective evaluation of code/solutions)
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Team Size
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Often larger, more hierarchical
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Smaller, elite, ruthlessly efficient (e.g., Durov's 30 engineers for a global company)
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        KPIs Targeted
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Cost-per-hire, time-to-hire, diversity metrics
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Skill-match accuracy, innovation rate, project success, team velocity
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        HR Involvement
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Extensive (Interviewing, onboarding, admin)
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Minimal (Challenge design, offer management)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 id="business-use-cases" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Business Use Cases
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                This model isn't just for tech giants; it offers significant value across various industries.
              </p>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Industry: Software Development & Engineering
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Difficulty finding truly elite, battle-tested engineers for critical projects, high cost of traditional tech recruitment, lengthy hiring cycles.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Rapid acquisition of top-tier talent with proven coding skills, reduced recruitment costs by up to 80% (e.g., from $15,000 to $3,000 per hire), formation of small, highly efficient "Navy SEAL" engineering teams that deliver high-impact innovation.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Industry: Data Science & Analytics
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Sourcing data scientists with advanced problem-solving capabilities in complex algorithms and machine learning, ensuring practical application over theoretical knowledge.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Identification of data scientists who can effectively solve real-world data challenges, leading to improved predictive models, enhanced data-driven decision-making, and faster development of AI/ML solutions.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Industry: Cybersecurity
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Identifying penetration testers or security architects with genuine hacking skills and the ability to find vulnerabilities where others can't.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Recruitment of highly skilled cybersecurity professionals with demonstrated offensive and defensive capabilities, strengthening organizational security postures and reducing the risk of breaches.
              </li>
              </ul>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Business Use Cases
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/844124/pexels-photo-844124.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Business Use Cases"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of business use cases concepts and implementation strategies.
                </p>
              </div>

              <h2 id="benefits-outcomes" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Benefits & Outcomes
              </h2>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Enhanced Code Quality: Direct selection based on functional, optimized code submissions ensures a high standard of technical proficiency from day one.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Faster Innovation Cycles: Elite teams, unburdened by extensive onboarding or skill gaps, can accelerate product development and deployment, leading to a 30-50% improvement in project velocity.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Reduced Technical Debt: By hiring individuals who demonstrate clean, efficient coding practices, companies can proactively mitigate future technical debt.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Optimal Skill-Fit: Candidates are selected based on their ability to solve specific, relevant technical problems, ensuring an almost perfect skill-to-role match.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Significant Cost Reduction: Elimination or drastic reduction of HR department overheads and recruitment agency fees, potentially cutting talent acquisition costs by 70-90%.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Increased Productivity: Small, highly skilled teams like Durov's 30 engineers can achieve output comparable to much larger traditional teams, boosting overall organizational productivity by as much as 200-300% per employee.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Unbiased Talent Selection: Meritocratic contests remove biases inherent in traditional methods, fostering a truly diverse and high-performing global talent pool.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Stronger Company Culture (for specific roles): For roles where autonomy and problem-solving are paramount, this model cultivates a culture of excellence, self-reliance, and continuous challenge.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Competitive Advantage: Access to a global pool of untapped talent provides a distinct competitive edge, allowing companies to quickly adapt and innovate.
              </li>
              </ul>

              <h2 id="challenges-realities" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Challenges & Realities
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Implementing a decentralized, skill-based hiring model isn't without its complexities. Designing effective and challenging contests requires significant technical expertise and upfront investment. Ensuring the integrity and fairness of global competitions demands robust platform security and anti-cheating measures. Integrating newly hired talent, who may have had minimal traditional HR interaction, into the existing company structure and culture can also present unique challenges, requiring a thoughtful approach to onboarding and team assimilation. Building a scalable infrastructure for these contests and managing a global talent pipeline requires strategic planning beyond just finding the best coders.
              </p>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Challenges & Realities
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Challenges & Realities"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of challenges & realities concepts and implementation strategies.
                </p>
              </div>

              <h2 id="future-outlook" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Future Outlook
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Over the next 12 months, we anticipate a continued surge in specialized online platforms facilitating skill-based hiring, moving beyond just coding. The trend will likely see an increase in "gamified" assessment modules, virtual hackathons, and project-based challenges across various professional domains, including design, data analytics, and even niche engineering fields. Companies will increasingly experiment with hybrid models, integrating aspects of decentralized sourcing into their existing HR strategies, aiming for a "best of both worlds" approach that combines efficiency with traditional support structures. Expect to see more CEOs and industry leaders openly discussing similar innovative HR strategies, potentially driving a new wave of talent acquisition techniques that prioritize demonstrable skill over traditional credentials.
              </p>

              <h2 id="conclusion" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Conclusion
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Pavel Durov's model of decentralized hiring via global coding contests stands as a powerful testament to the potential of meritocratic talent acquisition. While not a universal panacea, it offers a compelling framework for identifying elite engineers and specialists with unparalleled efficiency and cost-effectiveness. By focusing purely on demonstrated skill, companies can build small, battle-tested teams capable of outsized impact, driving innovation, and achieving remarkable operational excellence. This approach challenges us to rethink our assumptions about recruitment, inviting a future where talent is truly measured by ability, not just credentials.
              </p>

              <h2 id="call-to-action" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Call to Action
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Intrigued by the prospect of transforming your talent acquisition strategy and building an elite, high-performing team? Contact us today for a complimentary consultation or to explore a Proof-of-Concept (POC) on how a customized skill-based decentralized hiring framework can benefit your organization. Let's unlock the full potential of global talent together.
              </p>


              {/* Key Takeaways Section - AEO Optimized */}
              <div className="key-takeaways-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginTop: '3rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>⚡</span>
                  Key Takeaways - Fast Implementation Insights
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {keyTakeaways.map((takeaway, idx) => (
                    <li key={idx} style={{
                      color: '#ffffff',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(20, 184, 166, 0.1)'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.125rem',
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(20, 184, 166, 0.2)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        flexShrink: 0
                      }}>{idx + 1}</span>
                      <span>{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* FAQ Section */}
              <h2 id="faq" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Frequently Asked Questions
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '3rem' }}>
                {faqData.map((faq, idx) => (
                  <div key={idx} style={{
                    background: '#121212',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #262626',
                    transition: 'border-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#14b8a6'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#262626'}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      fontFamily: 'Sora, sans-serif',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{ color: '#14b8a6', flexShrink: 0 }}>Q{idx + 1}.</span>
                      <span>{faq.question}</span>
                    </h3>
                    <p style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                      lineHeight: '1.75',
                      marginLeft: '2rem',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA Section */}
              <div style={{
                marginTop: '3rem',
                background: '#121212',
                borderRadius: '16px',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                border: '1px solid #262626',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '384px',
                  height: '384px',
                  background: 'rgba(20, 184, 166, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(80px)'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '1rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>
                    Ready to Transform Your Business?
                  </h3>
                  <p style={{
                    color: '#e5e7eb',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    marginBottom: '1.5rem',
                    maxWidth: '600px',
                    margin: '0 auto 1.5rem',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    Contact us today for a personalized consultation and discover how we can help you achieve your goals.
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-block',
                      padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem)',
                      border: '2px solid #14b8a6',
                      color: '#14b8a6',
                      borderRadius: '8px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.3s',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#14b8a6';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#14b8a6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Get Started Today
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: 'clamp(1.5rem, 4vw, 2rem)',
            right: 'clamp(1.5rem, 4vw, 2rem)',
            width: 'clamp(44px, 10vw, 48px)',
            height: 'clamp(44px, 10vw, 48px)',
            background: '#121212',
            border: '2px solid #262626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#14b8a6',
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 50
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#14b8a6';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#262626';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default Blog26032026;
